import React, { useEffect, useState } from "react";
import CommanTable from "../../components/table/CommanTable";
import {
  ApartmentOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  addOrganizationData,
  editOrganizationData,
  getOrganizationData,
} from "../../redux/slices/organizationSlice";
import { useNavigate } from "react-router-dom";
import {
  setActionName,
  setCloseBtnName,
  setIsPopUpShow,
  setPopUpTitle,
  setSubmitBtnName,
} from "../../redux/slices/popUpSlice";
import { Button, Form, Input, message } from "antd";
import AppSecureStorage from "../../services/secureStorage";

const storage = new AppSecureStorage();

export const Organization = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [editingOrgId, setEditingOrgId] = useState(null);
  const [userData, setUserData] = useState({
    name: "",
    admin: "",
    domain: "",
  });

  const { page, limit, sortConfig, searchInput } = useSelector(
    (state) => state.globalReducer
  );
  const { organisations } = useSelector((state) => state.organizationReducer);
  const { closeBtnName, submitBtnName, isPopUpShow } = useSelector(
    (state) => state.popUpRedducer
  );

  // const [visibleColumns, setVisibleColumns] = useState({
    // sNo: true,
    // name: true,
    // domain: true,
    // companyEmail: true,
    // status: true,
  // });

  const [visibleColumns, setVisibleColumns] = useState(() => {
      // Load from localStorage on mount, fallback to default
      const saved = storage.get(`visibleColumns_${'organization'}`); // Use title for per-table persistence
      return saved
        ? JSON.parse(saved)
        : {
            sNo: true,
            name: true,
            domain: true,
            companyEmail: true,
            status: true,
          };
      });
  
  useEffect(() => {
    storage.set(`visibleColumns_${'organization'}`, JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    dispatch(
      getOrganizationData({
        page: page,
        limit: limit,
        navigate: navigate,
        searchQuery: searchInput || "",
        sortKey: sortConfig.key,
        sortDirection: sortConfig.direction,
      })
    );
  }, []);

  const handlePopUpOpen = () => {
    setEditingOrgId(null);
    dispatch(setCloseBtnName("Cancel"));
    dispatch(setSubmitBtnName("Submit"));
    dispatch(setActionName("organization"));
    dispatch(setPopUpTitle("Add Organaization"));
    dispatch(setIsPopUpShow(true));
    form.resetFields();
    form.setFieldsValue({ name: "", admin: "", domains: [""] });
    setUserData({ name: "", admin: "", domains: [""] });
  };

  const handleEditOrganization = (record) => {
    setEditingOrgId(record._id);
    dispatch(setCloseBtnName("Cancel"));
    dispatch(setSubmitBtnName("Submit"));
    dispatch(setActionName("organization"));
    dispatch(setPopUpTitle("Edit Organization"));
    dispatch(setIsPopUpShow(true));
    form.setFieldsValue({
      name: record.name || "",
      admin: record.admin || "",
      domains:
        record?.domains?.length > 0
          ? record.domains
          : record?.domain
            ? [record.domain]
            : [""],
    });
    setUserData({
      name: record.name || "",
      admin: record.admin || "",
      domains: record.domains || [""],
    });
  };

  const handleChangeInput = (e) => {
    const { name, value } = e.target;
    setUserData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    // Clear validation error for the field when user types
    form.setFields([{ name, errors: [] }]);
  };

  const resetPopupState = () => {
    dispatch(setIsPopUpShow(false));
    setEditingOrgId(null);
    form.resetFields();
    setUserData({ name: "", admin: "", domains: [""] });
    form.setFieldsValue({
      domains: [""],
    });
  };

  const onNext = async () => {
    try {
      const values = await form.validateFields();

      const payload = {
        ...values,
        domains: values.domains?.map((domain) => domain.trim()).filter(Boolean),
      };

      if (editingOrgId) {
        await dispatch(
          editOrganizationData({
            id: editingOrgId,
            body: payload,
            navigate,
          }),
        ).unwrap();
      } else {
        await dispatch(
          addOrganizationData({
            payload,
            navigate,
          }),
        ).unwrap();
      }
      resetPopupState();
    } catch (errorInfo) {
      console.log("Validation failed:", errorInfo);
    }
  };

  const handleOk = () => {
    resetPopupState();
  };

  const actionsOptions = [
    {
      label: "Edit",
      acttion: (record) => handleEditOrganization(record),
      icon: <EditOutlined />,
    },
  ];

  const renderPopupContent = () => {
    const handleAddDomain = (add) => {
      const domains = form.getFieldValue("domains") || [];

      const hasEmptyDomain = domains.some((domain) => !domain?.trim());

      if (hasEmptyDomain) {
        message.warning("Please fill all existing domains first");
        return;
      }
      const uniqueDomains = new Set(
        domains.map((domain) => domain.toLowerCase()),
      );

      if (uniqueDomains.size !== domains.length) {
        message.warning("Duplicate domains are not allowed");
        return;
      }
      add("");
    };
    return (
      <Form
        form={form}
        layout="vertical"
        initialValues={{ name: "", admin: "", domains: [""] }}
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[
            {
              required: true,
              message: "Please enter organization name",
            },
            {
              whitespace: true,
              message: "Name cannot be empty or contain only spaces",
            },
          ]}
        >
          <Input
            size="large"
            placeholder="Enter Name"
            prefix={<UserOutlined />}
          />
        </Form.Item>
        <Form.Item
          label="Admin"
          name="admin"
          rules={[
            {
              required: true,
              message: "Please enter admin email",
            },
            {
              type: "email",
              message: "Please enter a valid email address",
            },
            {
              whitespace: true,
              message: "Admin email cannot be empty or contain only spaces",
            },
          ]}
        >
          <Input
            size="large"
            placeholder="Enter Admin Email"
            prefix={<UserOutlined />}
          />
        </Form.Item>
        <Form.Item required label="Domains">
          <Form.List name="domains">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div
                    key={key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Form.Item
                      {...restField}
                      name={name}
                      style={{ flex: 1 }}
                      rules={[
                        {
                          required: true,
                          message: "Please enter domain",
                        },
                        {
                          whitespace: true,
                          message:
                            "Domain cannot be empty or contain only spaces",
                        },
                        {
                          pattern:
                            /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
                          message: "Please enter a valid domain name",
                        },
                        {
                          validator: (_, value) => {
                            const domains = form.getFieldValue("domains") || [];

                            const count = domains.filter(
                              (item) =>
                                item &&
                                value &&
                                item.toLowerCase() === value.toLowerCase(),
                            ).length;

                            if (count > 1) {
                              return Promise.reject(
                                new Error("Duplicate domain is not allowed"),
                              );
                            }

                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        size="large"
                        placeholder="Enter Domain"
                        prefix={<UserOutlined />}
                      />
                    </Form.Item>

                    {fields.length > 1 && (
                      <Button
                        danger
                        style={{ marginBottom: "12px" }}
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                      />
                    )}
                  </div>
                ))}

                <Form.Item>
                  <Button
                    type="dashed"
                    block
                    icon={<PlusOutlined />}
                    onClick={() => handleAddDomain(add)}
                  >
                    Add Domain
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form.Item>
      </Form>
    );
  };

  const renderFooter = () => (
    <div className="button-groups">
      <Button onClick={handleOk}>{closeBtnName}</Button>
      <Button onClick={onNext}>{submitBtnName}</Button>
    </div>
  );

  return (
    <CommanTable
      title="Organization"
      data={organisations?.data}
      visibleColumns={visibleColumns}
      setVisibleColumns={setVisibleColumns}
      icon={<ApartmentOutlined className="icon-size text-white" />}
      apiCall={(page, limit, searchInput) => {
        dispatch(
          getOrganizationData({
            page: page,
            limit: limit,
            navigate: navigate,
            searchQuery: searchInput || "",
            sortKey: sortConfig.key,
            sortDirection: sortConfig.direction,
          })
        );
      }}
      openAddPopup={handlePopUpOpen}
      renderPopupContent={renderPopupContent}
      isFooter={true}
      customFooter={renderFooter}
      actionsOptions={actionsOptions}
      isPopUpShow={isPopUpShow}
    />
  );
};
